import { useEffect, useState } from "react";
import _ from "lodash";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { enqueueSnackbar } from "notistack";
import dayjs from "dayjs";
import { Box, Grid2, Link, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { createAttachment } from "../../../../../apis";
import { serializeDayForAPI } from "../../../../../helpers/dateTransform";
import { returnIsValid, useConstants } from "../Main/useConstants";
import { EGSwitch } from "../../../../MainComponents/Switch/Switch";
import {
  InvestigationButton,
  InvestigationItemContainer,
  Separator,
  TemporalUploadedFile,
  InvestigationCollapsable,
} from "../components";
import { RichTextEditor } from "../../../../MainComponents/RichTextEditor/RichTextEditor";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { AddFile } from "./Modals/AddFile";
import { AddMeasure } from "./Modals/AddMeasure";
import {
  ProposedActionDetails,
  ProposedActionTitle,
} from "./ProposedActionSummary";
import { Conclude } from "./Modals/Conclude";
import EGDatePicker from "../../../../MainComponents/DatePicker/EGDatePicker";
import { EGBasicSelect } from "../../../../MainComponents/Select/EGBasicSelect";

// Funcion revisar el formulario y retornar si es valido y cuales son los errores
const validateForm = (conclusion) => {
  const errors = {};

  const text = conclusion.replace(/<[^>]*>/g, "").trim();
  if (text.length === 0) {
    errors.conclusion = true;
  }

  return [Object.entries(errors).length === 0 ? true : false, errors];
};

export const AddConclusion = ({
  conclusion,
  // setSelectedConclusion,
  setIsEditing,
  handleSaveConclusionDetails,
  isLoading,
  setIsLoading,
  involved,
  caseTypes = [],
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();
  const {
    conclusion: { verifiedOpts },
  } = useConstants();

  const [richTextEditor, setRichTextEditor] = useState("");
  const [comment, setComment] = useState("");
  // Resultado: comprobada, no comprobada o parcialmente
  const [verified, setVerified] = useState("");
  const [date, setDate] = useState(null);
  // Files from DB
  const [uploadedFiles, setUploadedFiles] = useState([]);
  // Local files
  const [files, setFiles] = useState([]);

  const [measures, setMeasures] = useState([]);
  const [triedToSave, setTriedToSave] = useState(false);
  const [errors, setErrors] = useState({});

  const [showAddConclusionFiles, setShowAddConclusionFiles] = useState(false);
  const [showAddConclusionMeasures, setShowAddConclusionMeasures] =
    useState(false);
  const [conclusionDate, setConclusionDate] = useState(false);
  const [addAdditionalObservations, setAddAdditionalObservations] =
    useState(false);
  const [selectedConclusionMeasure, setSelectedConclusionMeasure] =
    useState(null);

  const [showConclude, setShowConclude] = useState(false);

  useEffect(() => {
    if (conclusion !== null) {
      let date = dayjs(conclusion.conclusion_at);
      setRichTextEditor(conclusion.conclusion);
      setConclusionDate(!!conclusion.conclusion_at);
      setAddAdditionalObservations(!!conclusion.comment);
      setComment(conclusion.comment);
      setVerified(conclusion.verified ?? "");
      setDate(date.isValid() ? date : null);
      setUploadedFiles(conclusion.files);
      setMeasures(conclusion.measures);
    } else {
      setRichTextEditor("");
      setComment("");
      setVerified("");
      setDate(null);
      setFiles([]);
      setMeasures([]);
    }
  }, [conclusion]);

  // Validar cada que se hace un cambio
  useEffect(() => {
    if (triedToSave) {
      const [_, errors] = validateForm(richTextEditor);
      setErrors(errors);
    }
  }, [richTextEditor]);

  // End of edition
  const handleClose = () => {
    setIsEditing(false);
  };

  // Send files to DB
  const sendFiles = async () => {
    let uploadedAttachments = [];

    if (files.length > 0) {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("attachments", file);
      });

      try {
        const resp = await createAttachment(formData);
        const attachments_id = resp.attachments_id;
        uploadedAttachments = attachments_id.map((id, idx) => ({
          file: id,
          name: files[idx].name,
          details: files[idx].details || "",
          size: files[idx].size,
          type: files[idx].type,
        }));
      } catch (error) {
        console.error(error);
        setIsLoading(false);
      }
    }

    const existingFiles = uploadedFiles.map((file) => ({
      details: file.details || "",
      file: file.file, // id file
      name: file.name,
      size: file.size,
      type: file.type,
      url: file.url,
    }));

    // Merging new and existing files
    return [...existingFiles, ...uploadedAttachments];
  };

  // Valida el formulario; muestra snackbar si falta lo requerido
  const isFormValid = () => {
    const [isValid, errors] = validateForm(richTextEditor);
    setErrors(errors);
    setTriedToSave(true);
    if (!isValid) {
      enqueueSnackbar({
        variant: "error",
        message: t("resources.missing_required_inputs"),
      });
    }
    return isValid;
  };

  // Guarda la conclusión (puede lanzar). Reutilizado por "Guardar" y por el modal de concluir
  const persistConclusion = async (conclusionAt) => {
    const newFiles = await sendFiles();
    setFiles(newFiles);
    setUploadedFiles([]);

    await handleSaveConclusionDetails({
      conclusion: richTextEditor,
      comment: comment ?? null,
      conclusion_at: serializeDayForAPI(conclusionAt),
      folder: {
        conclusion: {
          files: newFiles,
          measures,
          verified: verified || null,
        },
      },
    });
  };

  // "Guardar": guarda sin concluir (mantiene la fecha del modo edición si la hay)
  const handleSave = async () => {
    if (!isFormValid()) return;
    setIsLoading(true);
    try {
      await persistConclusion(date || null);
      handleClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // "Guardar y concluir": valida y abre el modal de confirmación de fecha
  const handleConcludeClick = () => {
    if (!isFormValid()) return;
    setShowConclude(true);
  };

  // Confirmación del modal: guarda con la fecha elegida. Relanza para que el modal no cierre si falla
  const handleConfirmConclude = async (conclusionAt) => {
    setIsLoading(true);
    try {
      await persistConclusion(conclusionAt);
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUploadedFile = (idx) => {
    const newUploadedFiles = _.cloneDeep(uploadedFiles);
    newUploadedFiles.splice(idx, 1);
    setUploadedFiles(newUploadedFiles);
  };

  const handleDeleteTemporalFile = (idx) => {
    const newFiles = _.cloneDeep(files);
    newFiles.splice(idx, 1);
    setFiles(newFiles);
  };

  const handleEditConclusionMeasure = (idx) => {
    setSelectedConclusionMeasure(idx);
    setShowAddConclusionMeasures(true);
  };

  const handleDeleteConclusionMeasure = (idx) => {
    const newConclusionMeasures = _.cloneDeep(measures);
    newConclusionMeasures.splice(idx, 1);
    setMeasures(newConclusionMeasures);
  };

  return (
    <>
      <Conclude
        show={showConclude}
        onHide={() => setShowConclude(false)}
        onConfirm={handleConfirmConclude}
        defaultDate={date}
        loading={isLoading}
      />
      <AddFile
        show={showAddConclusionFiles}
        onHide={() => setShowAddConclusionFiles(false)}
        files={files}
        setFiles={setFiles}
      />
      <AddMeasure
        show={showAddConclusionMeasures}
        onHide={() => setShowAddConclusionMeasures(false)}
        measures={measures}
        setMeasures={setMeasures}
        selectedConclusionMeasure={selectedConclusionMeasure}
        setSelectedConclusionMeasure={setSelectedConclusionMeasure}
        involved={involved}
        caseTypes={caseTypes}
      />
      <Box padding={2}>
        <Separator
          text={
            conclusion !== null
              ? t("Conclusion.edit_conclusion")
              : t("Conclusion.add_conclusion")
          }
        />

        <Grid2 container spacing={2} mt={2}>
          <Grid2
            size={{ xs: 12, lg: 6 }}
            display="flex"
            alignItems="center"
            justifyContent="space-around"
          >
            <Typography
              fontSize={16}
              fontWeight={600}
              color={theme.palette.secondary.main}
            >
              {t("Conclusion.set_conclusion_date")}
            </Typography>
            <EGSwitch
              size="small"
              checked={conclusionDate}
              onChange={(e) => setConclusionDate(e.target.checked)}
            />
          </Grid2>

          <Grid2
            size={{ xs: 12, lg: 6 }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            px={1}
          >
            {conclusionDate && (
              <>
                <EGDatePicker
                  label={`${t("Conclusion.date")}:`}
                  value={date}
                  onChange={setDate}
                  minDate={dayjs()}
                />
              </>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, lg: 6 }}>
            <EGBasicSelect
              label={t("Conclusion.verified")}
              value={verified}
              options={verifiedOpts}
              onChange={(e) => setVerified(e.target.value)}
              isValid={null}
              showHelpText={false}
            />
          </Grid2>

          <Grid2 size={12}>
            <RichTextEditor
              label={t("Conclusion.investigation_conclusion")}
              value={richTextEditor}
              onChange={(value) => setRichTextEditor(value)}
              isValid={returnIsValid(errors?.conclusion, triedToSave)}
            />
          </Grid2>

          <Grid2 size={12}>
            {addAdditionalObservations ? (
              <Box>
                <EGInput
                  label={`${t("Conclusion.additional_observations")}`}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  type="textarea"
                  multiline
                  rows={5}
                />
              </Box>
            ) : (
              <Box display="flex" justifyContent="flex-end" mt={-1}>
                <Link
                  onClick={() => setAddAdditionalObservations(true)}
                  underline="hover"
                >
                  + {t("Conclusion.add_additional_observations")}
                </Link>
              </Box>
            )}
          </Grid2>
        </Grid2>

        <Stack
          spacing={2}
          mt={2}
          p={2}
          borderRadius="5px"
          border={`1px solid ${theme.palette.primary.main}`}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <InvestigationItemContainer
              color={theme.palette.primary.main}
              width="fit-content"
              sx={{ padding: 0.8 }}
            >
              <Typography fontSize={13} fontWeight={600}>
                {t("Conclusion.actions_of_conclusion")}
              </Typography>
            </InvestigationItemContainer>

            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="flex-end"
              spacing={1}
            >
              <InvestigationButton
                label={`${t("Conclusion.upload_files")}`}
                icon={false}
                size="medium"
                onClick={() => setShowAddConclusionFiles(true)}
              />
              <InvestigationButton
                label={`${t("Conclusion.measures_corrective")}`}
                size="medium"
                icon={false}
                onClick={() => setShowAddConclusionMeasures(true)}
              />
            </Stack>
          </Stack>

          {(uploadedFiles.length > 0 ||
            measures.length > 0 ||
            files.length > 0) && <hr style={{ margin: 0 }} />}

          {(uploadedFiles.length > 0 || files.length > 0) && (
            <>
              <Separator
                text={t("Conclusion.support_files_description_for_conclusion")}
              />
              <Grid2 container spacing={1}>
                {uploadedFiles.map((file, idxFile) => (
                  <Grid2
                    size={{ xs: 12, md: 6 }}
                    key={`uploaded-file-${idxFile}`}
                  >
                    <TemporalUploadedFile
                      name={file.name}
                      description={file.details}
                      size={file.size}
                      iconName="trash_can"
                      iconAction={() => handleDeleteUploadedFile(idxFile)}
                      iconColor={theme.palette.red.main}
                      width="100%"
                    />
                  </Grid2>
                ))}
                {files.map((file, idxFile) => (
                  <Grid2
                    size={{ xs: 12, md: 6 }}
                    key={`temporal-file-${idxFile}`}
                  >
                    <TemporalUploadedFile
                      name={file.name}
                      description={file.details}
                      size={file.size}
                      iconName="trash_can"
                      iconAction={() => handleDeleteTemporalFile(idxFile)}
                      iconColor={theme.palette.red.main}
                      width="100%"
                    />
                  </Grid2>
                ))}
              </Grid2>
            </>
          )}

          {measures.length > 0 && (
            <>
              <Separator
                text={t("Conclusion.measures_corrective_description")}
              />
              <DragDropContext
                onDragEnd={(result) => {
                  if (!result.destination) return;

                  const items = Array.from(measures);
                  const [reorderedItem] = items.splice(result.source.index, 1);
                  items.splice(result.destination.index, 0, reorderedItem);

                  setMeasures(items);
                }}
              >
                <Droppable droppableId="conclusionMeasures">
                  {(provided) => (
                    <Stack {...provided.droppableProps} ref={provided.innerRef}>
                      {measures.map((conclusionMeasure, idx) => (
                        <Draggable
                          key={idx}
                          draggableId={`conclusionMeasure-${idx}`}
                          index={idx}
                        >
                          {(provided, snapshot) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                opacity: snapshot.isDragging ? 0.8 : 1,
                              }}
                              sx={{ my: 0.5 }}
                            >
                              <InvestigationCollapsable
                                title={
                                  <ProposedActionTitle
                                    measure={conclusionMeasure}
                                  />
                                }
                                details={
                                  <ProposedActionDetails
                                    measure={conclusionMeasure}
                                  />
                                }
                                labelCollapse={t("Conclusion.details")}
                                dragHandleProps={provided.dragHandleProps}
                                buttons={[
                                  {
                                    tooltip: t("Conclusion.delete"),
                                    name: "trash_can",
                                    color: theme.palette.red.main,
                                    onClick: () =>
                                      handleDeleteConclusionMeasure(idx),
                                  },
                                  {
                                    tooltip: t("Conclusion.edit"),
                                    name: "outline_edit",
                                    color: theme.palette.primary.main,
                                    onClick: () =>
                                      handleEditConclusionMeasure(idx),
                                  },
                                ]}
                              />
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Stack>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          )}
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="flex-end"
          spacing={1}
          mt={2}
        >
          {/* Con fecha de conclusión activa sólo se concluye (confirma en el modal) */}
          {!conclusionDate && (
            <InvestigationButton
              label={`${t("Conclusion.save")}`}
              variant="success"
              size="medium"
              icon="mark_check_bold"
              onClick={() => handleSave()}
              loading={isLoading}
            />
          )}
          <InvestigationButton
            label={`${t("Conclusion.save_and_conclude")}`}
            variant="danger"
            size="medium"
            icon="mark_check_bold"
            onClick={handleConcludeClick}
            loading={isLoading}
          />
          <InvestigationButton
            label={`${t("Conclusion.cancel")}`}
            icon="close_icon"
            size="medium"
            onClick={handleClose}
            loading={isLoading}
          />
        </Stack>
      </Box>
    </>
  );
};
